<template>
  <div class="tenant-access-page">
    <div class="page-header">
      <div class="page-header__copy">
        <p class="eyebrow">TENANT CONTROL</p>
        <h1>権限管理</h1>
        <p>
          社員に割り当てる権限グループと、各グループに含める権限を管理します。
          操作できるのは「契約企業管理者（自社最上位）」と「社内システム管理者」のみです。※PREIKA運営側の権限は対象外です。
        </p>
      </div>
      <CButton
        class="page-header__action"
        color="primary"
        :disabled="!canEdit"
        :title="canEdit ? null : editBlockedReason"
        @click="openCreateRole"
      >
        権限グループを追加
      </CButton>
    </div>

    <CAlert v-if="successMessage" color="success" close-button @update:show="successMessage = ''">
      {{ successMessage }}
    </CAlert>
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>
    <TenantOwnerManagement />

    <CAlert v-if="!loading" color="info" class="plan-scope-alert">
      現在の契約プランは<strong>{{ currentPlanName }}</strong>です。
      権限グループに追加できるのは、ご契約プランまたは個別契約で利用可能な権限のみです。
      対象外の権限はグレー表示となり選択できません。
    </CAlert>

    <CCard class="access-card">
      <CCardHeader>
        <strong>社員ごとの権限グループ</strong>
        <div v-if="!canAssign" class="text-muted small mt-1">{{ assignBlockedReason }}</div>
        <!--
          「契約企業管理者（自社最上位）」は、下の選択欄では選べない（押しても必ず断られる）。
          なぜ選べないか・どこから行うかを、選択欄のあるこのカードの頭に1度だけ書く。
          出すのは、上の「契約企業管理者」の節が見える人（最上位の管理者）にだけ。
        -->
        <div v-if="canAssign && canManageProtected" class="text-muted small mt-1 owner-by-invitation-note">契約企業管理者は下記一覧で変更できません。追加・解除は上の「契約企業管理者」から行ってください。</div>
      </CCardHeader>
      <CCardBody>
        <div v-if="loading" class="text-center py-5"><CSpinner color="primary"/></div>
        <template v-else>
          <div class="desktop-only responsive-table-shell">
            <CDataTable
              :items="normalizedUsers"
              :fields="userFields"
              bordered
              striped
              hover
              pagination
              :items-per-page="10"
            >
              <template #roles="{item}">
                <td>
                  <div class="role-badge-list">
                    <CBadge v-for="code in item.roleCodes" :key="code" color="info">
                      {{ roleLabel(code) }}
                    </CBadge>
                    <span v-if="!item.roleCodes.length" class="text-muted">権限グループ未設定</span>
                  </div>
                  <!--
                    **社員の行は、割り当て中の権限グループの名前だけを出す。**権限の中身は下の「自社の権限グループ一覧」の表で
                    見る（2026-09-27）。社員ごとに「付与されている権限」を並べた版は、「一人一人の書き方は変えなくていい。
                    権限グループ一覧のところが歯抜けだからわからんと言った」と言われて戻した。
                    会社が中身を編集した権限グループでも、札は権限グループの名前のまま（「編集した」などを添えない）。

                    その人の左メニューに何が出るかは、ここで見せる（左メニューと同じ関数で計算する）。
                  -->
                  <details v-if="item.roleCodes.length && menusReady" class="menu-details">
                    <summary>表示されるメニュー（{{ menusForUser(item).length }}件）</summary>
                    <div class="menu-group-list">
                      <div v-for="group in groupMenus(menusForUser(item))" :key="group.title" class="menu-group-row">
                        <strong v-if="group.title">{{ group.title }}</strong>
                        <span><span v-for="(name, i) in group.names" :key="name" class="menu-group-row__name" lang="ja">{{ name }}{{ i < group.names.length - 1 ? '、' : '' }}</span></span>
                      </div>
                    </div>
                  </details>
                </td>
              </template>
              <template #actions="{item}">
                <td>
                  <div class="desktop-role-controls">
                    <CSelect
                      :options="roleOptions"
                      :value="selectedRoleByUser[item.userId]"
                      :disabled="!canAssign"
                      @update:value="value => setSelectedRole(item.userId, value)"
                    />
                    <div class="desktop-role-buttons">
                      <CButton size="sm" color="primary" :disabled="saving || !canAssign" @click="assignRole(item)">追加</CButton>
                      <CButton size="sm" color="danger" variant="outline" :disabled="saving || !canAssign" @click="removeRole(item)">外す</CButton>
                    </div>
                    <!--
                      **結果は押したボタンの下に出す。**画面の上の帯へ出していた頃は、
                      表が 1,000px 以上下にあるので押した人から見えず、「追加を押しても
                      何も反応しない」と言われた（2026-09-26）。
                    -->
                    <p
                      v-if="rowMessages[item.userId]"
                      role="status"
                      :class="['row-message', 'row-message--' + rowMessages[item.userId].type]"
                    >
                      {{ rowMessages[item.userId].text }}
                    </p>
                  </div>
                </td>
              </template>
            </CDataTable>
          </div>

          <div class="mobile-only">
            <div class="mobile-record-list" aria-label="社員ごとの権限グループ一覧">
              <article v-for="user in mobileUsers" :key="user.userId" class="mobile-record-card">
                <div class="mobile-record-card__header">
                  <strong>{{ user.name || '氏名未設定' }}</strong>
                  <span>{{ user.email || 'メールアドレス未設定' }}</span>
                </div>

                <section class="mobile-section">
                  <h3>割り当て中の権限グループ</h3>
                  <div class="role-badge-list">
                    <CBadge v-for="code in user.roleCodes" :key="code" color="info">
                      {{ roleLabel(code) }}
                    </CBadge>
                    <span v-if="!user.roleCodes.length" class="text-muted">権限グループ未設定</span>
                  </div>
                  <details v-if="user.roleCodes.length && menusReady" class="menu-details">
                    <summary>表示されるメニュー（{{ menusForUser(user).length }}件）</summary>
                    <div class="menu-group-list">
                      <div v-for="group in groupMenus(menusForUser(user))" :key="group.title" class="menu-group-row">
                        <strong v-if="group.title">{{ group.title }}</strong>
                        <span><span v-for="(name, i) in group.names" :key="name" class="menu-group-row__name" lang="ja">{{ name }}{{ i < group.names.length - 1 ? '、' : '' }}</span></span>
                      </div>
                    </div>
                  </details>
                </section>

                <section class="mobile-section mobile-section--controls">
                  <label>権限グループを変更</label>
                  <CSelect
                    :options="roleOptions"
                    :value="selectedRoleByUser[user.userId]"
                    :disabled="!canAssign"
                    @update:value="value => setSelectedRole(user.userId, value)"
                  />
                  <div class="mobile-button-grid">
                    <CButton color="primary" :disabled="saving || !canAssign" @click="assignRole(user)">追加</CButton>
                    <CButton color="danger" variant="outline" :disabled="saving || !canAssign" @click="removeRole(user)">外す</CButton>
                  </div>
                  <p
                    v-if="rowMessages[user.userId]"
                    role="status"
                    :class="['row-message', 'row-message--' + rowMessages[user.userId].type]"
                  >
                    {{ rowMessages[user.userId].text }}
                  </p>
                </section>
              </article>

              <div v-if="!normalizedUsers.length" class="mobile-empty-state">
                対象の社員がいません。
              </div>
            </div>

            <nav v-if="mobileUserPageCount > 1" class="mobile-pager" aria-label="社員一覧のページ">
              <CButton
                color="secondary"
                variant="outline"
                :disabled="mobileUserPage <= 1"
                @click="setMobileUserPage(mobileUserPage - 1)"
              >
                前へ
              </CButton>
              <span>{{ mobileUserPage }} / {{ mobileUserPageCount }}</span>
              <CButton
                color="secondary"
                variant="outline"
                :disabled="mobileUserPage >= mobileUserPageCount"
                @click="setMobileUserPage(mobileUserPage + 1)"
              >
                次へ
              </CButton>
            </nav>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CCard class="access-card">
      <CCardHeader>
        <strong>自社の権限グループ一覧</strong>
        <div class="text-muted small mt-1">
          一般社員・事務担当者などの標準の権限グループも、自社用に中身を変更できます。他社の設定には影響しません。
        </div>
        <div v-if="!canEdit" class="text-muted small mt-1">{{ editBlockedReason }}</div>
      </CCardHeader>
      <CCardBody>
        <!--
          **権限を縦、権限グループを横に並べた ○／− の表（2026-09-27）。**

          以前は権限グループごとに「含まれる権限」の名前だけを並べていて、持っていない権限は書かれなかった。
          権限グループごとに並ぶ数も位置も違うので、見比べられない（「権限グループ一覧のところが歯抜けだからわからん」）。
          全部の権限を同じ並び（見出しごと。権限の編集の小窓と同じ）で出し、持っていないものも「−」で出す。

          - 会社の契約に含まれない機能の権限は、行ごと灰色にする（編集の小窓のグレー表示と同じ作り。
            名前にも同じ理由の添え書きが付く）。その行の ○ は「契約対象外として保持」している権限
          - ○／− は、どの列も /roles が返す権限（r_role_permission）から決める。最上位の管理者も決め打ちしない。
            back も最上位の管理者を特別扱いせず、同じ行から権限を読む（AccessControlService）。
            行がそろった DB なら全部 ○ になる（配っているのは 20260723_seed_internal_account_plan_rbac.sql の
            CROSS JOIN と、あとから足した権限ごとの migration）。行が欠けた DB で ○ と出すと、
            同じ列の「表示されるメニュー」（本当の権限から計算する）と食い違うので、欠けは − のまま見せる
          - 権限グループが持っているのに /permissions に無い権限も、黙って消さずに行を足す（matrixPermissionGroups）
          - 列の名前は権限グループの名前そのまま。会社が中身を編集していても何も添えない
        -->
        <div class="desktop-only responsive-table-shell">
          <table v-if="normalizedRoles.length" class="table table-sm table-bordered role-matrix">
            <thead>
              <tr>
                <th scope="col" class="role-matrix__corner">含まれる権限</th>
                <th v-for="role in normalizedRoles" :key="role.id" scope="col" class="role-matrix__role-name">{{ role.name }}</th>
              </tr>
            </thead>
            <!--
              見出しは下に並ぶ行（この tbody）の見出しなので rowgroup。
              見出しの欄は表の幅いっぱいにまたがるので、文字だけを span で包んで左端に留める
              （権限グループが多くて横へ送っても、見出しが画面の外へ流れない）。
            -->
            <tbody v-for="group in matrixPermissionGroups" :key="group.key">
              <tr class="role-matrix__heading">
                <th scope="rowgroup" :colspan="normalizedRoles.length + 1">
                  <span class="role-matrix__heading-text">
                    {{ group.title }}
                    <span v-if="group.note" class="role-matrix__note">{{ group.note }}</span>
                  </span>
                </th>
              </tr>
              <tr
                v-for="permission in group.items"
                :key="permission.code"
                :class="{ 'role-matrix__row--unavailable': !permission.available }"
              >
                <!-- 添え書きは名前の下の行に分ける（名前と続けて折り返すと「ビジネス以｜上」のように語の途中で切れた） -->
                <th scope="row" class="role-matrix__permission">{{ permission.label }}<span v-if="permissionAvailabilityNote(permission)" class="role-matrix__availability">{{ permissionAvailabilityNote(permission) }}</span></th>
                <td
                  v-for="role in normalizedRoles"
                  :key="role.id"
                  :class="['role-matrix__mark', roleHasPermission(role, permission.code) ? 'role-matrix__mark--has' : 'role-matrix__mark--not']"
                  :title="markTitle(role, permission)"
                >
                  {{ roleHasPermission(role, permission.code) ? markHas : markNot }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="role-matrix__actions">
                <td class="role-matrix__footer-label"></td>
                <td v-for="role in normalizedRoles" :key="role.id" :data-role-id="role.id">
                  <template v-if="role.code !== 'TENANT_OWNER'">
                    <CButton
                      size="sm"
                      color="info"
                      variant="outline"
                      :disabled="!canEditRole(role)"
                      :title="canEditRole(role) ? null : editRoleBlockedReason(role)"
                      @click="openPermissionEdit(role)"
                    >
                      権限を編集
                    </CButton>
                    <p v-if="canEdit && !canEditRole(role)" class="text-muted small mt-1 mb-0">{{ editRoleBlockedReason(role) }}</p>
                  </template>
                  <span v-else class="text-muted">最上位の管理者は変更不可</span>
                  <!-- 保存した結果は、押したボタンの下に出す（画面の上の帯は、表から遠くて見えない）。 -->
                  <p
                    v-if="roleMessages[role.code]"
                    role="status"
                    :class="['row-message', 'row-message--' + roleMessages[role.code].type]"
                  >
                    {{ roleMessages[role.code].text }}
                  </p>
                </td>
              </tr>
              <tr class="role-matrix__menus">
                <th scope="row" class="role-matrix__footer-label">表示されるメニュー</th>
                <td v-for="role in normalizedRoles" :key="role.id" :data-role-id="role.id">
                  <!-- 最上位の管理者は全部のメニューが出て長いので、社員の行と同じく折りたたむ。 -->
                  <details v-if="menusReady && role.menus.length" class="menu-details menu-details--in-table">
                    <summary>表示されるメニュー（{{ role.menus.length }}件）</summary>
                    <div class="menu-group-list">
                      <div v-for="menuGroup in groupMenus(role.menus)" :key="menuGroup.title" class="menu-group-row">
                        <strong v-if="menuGroup.title">{{ menuGroup.title }}</strong>
                        <span><span v-for="(name, i) in menuGroup.names" :key="name" class="menu-group-row__name" lang="ja">{{ name }}{{ i < menuGroup.names.length - 1 ? '、' : '' }}</span></span>
                      </div>
                    </div>
                  </details>
                  <span v-else class="text-muted">{{ menusReady ? '表示できるメニューはありません' : menusUnknownText }}</span>
                </td>
              </tr>
            </tfoot>
          </table>
          <p v-else-if="!loading" class="text-muted mb-0">使える権限グループがありません。</p>
        </div>

        <!--
          スマートフォンでは権限グループごとのカードにし、カードの中で上の表と同じ並びの全部の権限を ○／− で出す
          （表を横に何列も並べると読めないので、表の DOM を CSS で変形せずに、専用のカードを描く）。
        -->
        <div class="mobile-only mobile-record-list" aria-label="自社の権限グループ一覧">
          <article v-for="role in normalizedRoles" :key="role.id" class="mobile-record-card">
            <div class="mobile-role-header">
              <strong>{{ role.name }}</strong>
            </div>

            <section class="mobile-section">
              <h3>表示されるメニュー</h3>
              <details v-if="menusReady && role.menus.length" class="menu-details menu-details--in-table">
                <summary>表示されるメニュー（{{ role.menus.length }}件）</summary>
                <div class="menu-group-list">
                  <div v-for="group in groupMenus(role.menus)" :key="group.title" class="menu-group-row">
                    <strong v-if="group.title">{{ group.title }}</strong>
                    <span><span v-for="(name, i) in group.names" :key="name" class="menu-group-row__name" lang="ja">{{ name }}{{ i < group.names.length - 1 ? '、' : '' }}</span></span>
                  </div>
                </div>
              </details>
              <span v-else class="text-muted">{{ menusReady ? '表示できるメニューはありません' : menusUnknownText }}</span>
            </section>

            <section class="mobile-section">
              <h3>含まれる権限</h3>
              <div v-for="group in matrixPermissionGroups" :key="group.key" class="mobile-permission-group">
                <strong>{{ group.title }}</strong>
                <p v-if="group.note" class="role-matrix__note">{{ group.note }}</p>
                <div
                  v-for="permission in group.items"
                  :key="permission.code"
                  :class="['mobile-permission-row', { 'role-matrix__row--unavailable': !permission.available }]"
                >
                  <span class="mobile-permission-row__name">{{ permissionDisplayLabel(permission) }}</span>
                  <span
                    :class="['role-matrix__mark', roleHasPermission(role, permission.code) ? 'role-matrix__mark--has' : 'role-matrix__mark--not']"
                    :title="markTitle(role, permission)"
                  >{{ roleHasPermission(role, permission.code) ? markHas : markNot }}</span>
                </div>
              </div>
            </section>

            <template v-if="role.code !== 'TENANT_OWNER'">
              <CButton
                class="mobile-primary-action"
                color="info"
                variant="outline"
                :disabled="!canEditRole(role)"
                @click="openPermissionEdit(role)"
              >
                権限を編集
              </CButton>
              <p v-if="!canEditRole(role)" class="text-muted small mt-2 mb-0">{{ editRoleBlockedReason(role) }}</p>
            </template>
            <div v-else class="protected-role-note">
              最上位の管理者はセキュリティ保護のため、この画面からは変更できません。
            </div>
            <p
              v-if="roleMessages[role.code]"
              role="status"
              :class="['row-message', 'row-message--' + roleMessages[role.code].type]"
            >
              {{ roleMessages[role.code].text }}
            </p>
          </article>

          <div v-if="!normalizedRoles.length" class="mobile-empty-state">
            使える権限グループがありません。
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!--
      **メニューから引く向きの一覧。**権限の側から「対象メニュー」を書くだけでは、
      「ユーザー管理を出すには何をオンにするのか」に答えられない（2026-09-26）。
      左メニューを出し分けている経路表から計算するので、左メニューを直せばここも変わる。
    -->
    <CCard class="access-card">
      <CCardHeader>
        <strong>メニューの表示に必要な権限一覧</strong>
        <div class="text-muted small mt-1">
          メニューごとに表示に必要な権限を掲載しています。なお、ご契約に含まれない機能のメニューは、権限をオンにしても表示されません。
        </div>
      </CCardHeader>
      <CCardBody>
        <div class="responsive-table-shell">
          <table class="table table-sm table-bordered menu-requirement-table">
            <thead>
              <tr>
                <th scope="col">見出し</th>
                <th scope="col">対象メニュー</th>
                <th scope="col">必要な権限</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in menuRequirements" :key="row.group + '/' + row.name">
                <td>{{ row.group || '―' }}</td>
                <td>{{ row.name }}</td>
                <td>{{ requirementText(row) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>

    <CModal
      title="新しい権限グループを追加"
      :show="roleModal"
      @update:show="closeCreateRole"
      size="lg"
      :close-on-backdrop="false"
      class="access-modal"
    >
      <!--
        **CInput の label 属性では必須の印を入れられない。**そのため label を
        外して、印つきの label を自分で書く。

        名前は必須のままにする。一覧でも社員への割り当てでも、
        **名前がその権限グループを指す唯一の手がかり**だから。印が無いと、空のまま
        作成を押して初めて必須だったと気づくことになる。
      -->
      <label>権限グループ名<span class="asterisk">*</span></label>
      <CInput v-model.trim="roleForm.roleName" placeholder="例：営業管理者"/>
      <CTextarea
        label="説明"
        v-model.trim="roleForm.description"
        placeholder="この権限グループを割り当てる対象や担当業務を入力してください。"
      />
      <CAlert color="info" class="mt-3">
        現在の契約プランは<strong>{{ currentPlanName }}</strong>です。
        グレー表示の権限は、この契約では選択できません。
      </CAlert>
      <h6 class="mt-4">含める権限</h6>
      <div v-for="group in guidedPermissionGroups" :key="group.key" class="permission-group">
        <h6>{{ group.title }}</h6>
        <p v-if="group.note" class="permission-group__note">{{ group.note }}</p>
        <div class="permission-grid">
          <div v-for="permission in group.items" :key="permission.code" class="permission-item">
            <CInputCheckbox
              class="permission-option"
              :class="{ 'permission-option--unavailable': !permission.available }"
              :label="permissionDisplayLabel(permission)"
              :value="permission.code"
              :disabled="!permission.available"
              :checked="roleForm.permissionCodes.includes(permission.code)"
              @update:checked="checked => togglePermission(roleForm.permissionCodes, permission, checked)"
            />
            <p v-if="permission.effect" class="permission-item__effect">{{ menusLabel }}{{ permission.effect }}</p>
            <p v-if="permission.onlyForRoles" class="permission-item__warning">
              「社内システム管理者」グループ未割り当ての社員には適用されません。
            </p>
          </div>
        </div>
      </div>
      <div v-if="menusReady" class="menu-preview">
        <strong>この権限グループで表示されるメニュー</strong>
        <div v-if="previewMenus(null, roleForm.permissionCodes).length" class="menu-group-list">
          <div v-for="group in groupMenus(previewMenus(null, roleForm.permissionCodes))" :key="group.title" class="menu-group-row">
            <strong v-if="group.title">{{ group.title }}</strong>
            <span><span v-for="(name, i) in group.names" :key="name" class="menu-group-row__name" lang="ja">{{ name }}{{ i < group.names.length - 1 ? '、' : '' }}</span></span>
          </div>
        </div>
        <span v-else class="text-muted">表示できるメニューはありません</span>
      </div>
      <!--
        **押せない理由も、断られた理由も、押せないボタンの隣に出す。**

        このモーダルは権限の一覧で長くなる。作成を押す時点で、名前の欄は
        画面の外にあることがほとんどで、**赤字を画面の上部（モーダルの外）へ
        入れても読めない。**諦めて閉じないと理由が分からない画面だった。
      -->
      <CAlert v-if="missingFields.length" color="warning" class="mb-3">
        <b>あと{{ missingFields.length }}項目入力すると、作成できます。</b>
        <ul class="mb-0 mt-2">
          <li v-for="what in missingFields" :key="what">{{ what }}</li>
        </ul>
      </CAlert>
      <CAlert v-if="roleFormError" color="danger" class="mb-3">
        {{ roleFormError }}
      </CAlert>
      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" :disabled="saving" @click="closeCreateRole">キャンセル</CButton>
          <CButton
            color="primary"
            :disabled="saving || !roleForm.roleName"
            @click="createRole"
          >
            作成
          </CButton>
        </div>
      </template>
    </CModal>

    <CModal
      title="権限の編集"
      :show="permissionModal"
      @update:show="closePermissionEdit"
      size="lg"
      :close-on-backdrop="false"
      class="access-modal"
    >
      <template v-if="editingRole">
        <p><strong>{{ editingRole.name }}</strong>に含める権限を選択してください。</p>
        <CAlert v-if="editingRole.standardRole && !editingRole.customized" color="info">
          保存すると、この会社専用の設定を作成します。他社の「{{ editingRole.name }}」には影響しません。
        </CAlert>
        <CAlert v-else color="secondary">
          この変更は現在の会社だけに適用されます。
        </CAlert>
        <CAlert color="info">
          現在の契約プランは<strong>{{ currentPlanName }}</strong>です。
          グレー表示の権限は新しく選択できません。
        </CAlert>
        <CAlert v-if="editingDormantPermissions.length" color="warning">
          現在の契約プランでは利用できない権限が{{ editingDormantPermissions.length }}件含まれています。
          契約を変更して再び使えるようになったときのために設定を保持しており、この画面からは変更できません。
        </CAlert>
      </template>
      <div v-for="group in guidedPermissionGroups" :key="group.key" class="permission-group">
        <h6>{{ group.title }}</h6>
        <p v-if="group.note" class="permission-group__note">{{ group.note }}</p>
        <div class="permission-grid">
          <div v-for="permission in group.items" :key="permission.code" class="permission-item">
            <CInputCheckbox
              class="permission-option"
              :class="{ 'permission-option--unavailable': !permission.available }"
              :label="permissionDisplayLabel(permission)"
              :value="permission.code"
              :disabled="!permission.available"
              :checked="editingPermissionCodes.includes(permission.code)"
              @update:checked="checked => togglePermission(editingPermissionCodes, permission, checked)"
            />
            <p v-if="permission.effect" class="permission-item__effect">{{ menusLabel }}{{ permission.effect }}</p>
            <p
              v-if="editingRole && permission.onlyForRoles && !permission.onlyForRoles.includes(editingRole.code)"
              class="permission-item__warning"
            >
              「社内システム管理者」グループ未割り当ての社員には適用されません。
            </p>
          </div>
        </div>
      </div>
      <div v-if="editingRole && menusReady" class="menu-preview">
        <strong>この権限グループで表示されるメニュー</strong>
        <div v-if="previewMenus(editingRole.code, editingPermissionCodes).length" class="menu-group-list">
          <div v-for="group in groupMenus(previewMenus(editingRole.code, editingPermissionCodes))" :key="group.title" class="menu-group-row">
            <strong v-if="group.title">{{ group.title }}</strong>
            <span><span v-for="(name, i) in group.names" :key="name" class="menu-group-row__name" lang="ja">{{ name }}{{ i < group.names.length - 1 ? '、' : '' }}</span></span>
          </div>
        </div>
        <span v-else class="text-muted">表示できるメニューはありません</span>
      </div>
      <!--
        **こちらのモーダルも、断られた理由は中へ出す。**

        追加のモーダルと同じ事故がここにも残っていた。更新が断られても
        このモーダルは閉じないので、赤字を画面の上部（モーダルの外）へ
        置くと暗幕の下に潜り、**押しても何も起きていないように見える。**

        しかも読もうとして閉じると、開き直したときに openPermissionEdit が
        サーバの内容で選び直すので、**やり直そうとするほど選んだ権限が
        消える。**理由が中にあれば、閉じずにそのまま直せる。
      -->
      <CAlert v-if="permissionFormError" color="danger" class="mb-3">
        {{ permissionFormError }}
      </CAlert>
      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" :disabled="saving" @click="closePermissionEdit">キャンセル</CButton>
          <CButton color="primary" :disabled="saving" @click="savePermissions">更新</CButton>
        </div>
      </template>
    </CModal>
  </div>
</template>

<script>
import TenantOwnerManagement from '@/components/access/TenantOwnerManagement'
import api from '@/services/tenantAccessManagement'
import { getAccessContext } from '@/services/accessContext'
import {
  canAssignTenantRoles,
  canEditTenantRoles,
  canManageProtectedTenantAdminRoles
} from '@/utils/accessPolicy'
import {
  PERMISSION_GROUPS,
  PERMISSION_GUIDE,
  MENUS_LABEL,
  effectText,
  menuPermissionTable,
  menusForGroup
} from '@/utils/permissionGuide'
import { sidebarMenuItems } from '@/utils/sidebarNavigation'

const ROLE_LABELS = {
  /*
    運営（PREIKA側）の権限グループ。一覧には出さないが、社員の札には本当の名前で出す。
    以前は「設定済みの権限グループ」と出ていて、何が付いているのか分からなかった（2026-09-26）。
  */
  PLATFORM_SUPER_ADMIN: 'PREIKA緊急最高管理者',
  PLATFORM_OPERATIONS_ADMIN: 'PREIKA運営管理者',
  PLATFORM_SUPPORT_VIEWER: 'PREIKAサポート閲覧者',
  TENANT_OWNER: '契約企業管理者（自社最上位）',
  TENANT_SYSTEM_ADMIN: '社内システム管理者',
  EXECUTIVE_ADMIN: '経営管理者',
  CLERK: '事務担当者',
  EMPLOYEE: '一般社員'
}

/**
 * 最上位の管理者だけが割り当て・変更できる権限グループ。
 *
 * **判定はこの部品の中で持つ。**以前は utils/accessManagementUiPolicy.js が、画面が描かれた
 * あとに DOM のボタンの文字（「自社用に変更」「解除」など）を探して押せなくしていた。
 * 文字を変えると目印が黙って外れて、押せてしまう（2026-09-26 に文言を変えるときに気づいた）。
 */
const PROTECTED_ROLE_CODES = ['TENANT_OWNER', 'TENANT_SYSTEM_ADMIN']
/*
 * 社員の行の［追加］［外す］からは、誰が押しても必ず断られる権限グループ。
 * 追加は招待（本人の受諾まで）、解除は再認証と理由入力を伴う管理者解除で行う
 * 決まりで、サーバが断る（割り当て：TenantOwnerInvitationService、解除：
 * TenantOwnerRemovalService）。選べるのに押すと断られる、を作らない。
 */
const INVITATION_ONLY_ROLE_CODES = ['TENANT_OWNER']

const UNCLASSIFIED_GROUP = '未分類'

/** 権限グループの一覧の表で、その権限を持つ／持たないを表す記号。 */
const MARK_HAS = '○'
const MARK_NOT = '−'
/** 契約に含まれない機能の権限を、権限グループに保存したまま持っているときの呼び名（ヘルプと同じ言葉） */
const DORMANT_MARK_TITLE = '契約対象外として保持'

export default {
  name: 'TenantAccessManagement',
  components: { TenantOwnerManagement },
  data () {
    return {
      menusLabel: MENUS_LABEL,
      markHas: MARK_HAS,
      markNot: MARK_NOT,
      menuRequirements: menuPermissionTable(),
      loading: false,
      saving: false,
      errorMessage: '',
      /*
        権限グループを作るモーダル専用の赤字。**画面上部の errorMessage とは分ける。**
        モーダルが開いている間、上部の赤字は覆われていて読めない。
      */
      roleFormError: '',
      /* 権限を選び直すモーダル専用の赤字。理由は roleFormError と同じ。 */
      permissionFormError: '',
      successMessage: '',
      users: [],
      roles: [],
      permissions: [],
      /*
        見ている本人の権限グループ・会社の機能・権限（/access-context）。
        押せる操作の判定と、「表示されるメニュー」の計算に使う。**読めなかったら null のまま**にして、
        操作は止め、メニューは「表示できません」と出す（空の一覧を「メニューが無い」と見せない）。
      */
      viewerContext: null,
      contextFailed: false,
      selectedRoleByUser: {},
      /*
        社員の行で押した［追加］［外す］の結果。**行のボタンの下に出す。**
        画面の上の帯（successMessage / errorMessage）は、表から遠くて見えない。
      */
      rowMessages: {},
      /*
        権限グループの行で保存した結果。理由は rowMessages と同じ。
        **id ではなくコードで結び付ける。**標準の権限グループを初めて保存すると、back は自社用の行を
        新しい id で作る（コードは同じ）。id で結び付けると、読み直した瞬間に結果の文が消えていた（2026-09-26 の監査）。
      */
      roleMessages: {},
      roleModal: false,
      permissionModal: false,
      editingRole: null,
      editingPermissionCodes: [],
      roleForm: { roleName: '', description: '', permissionCodes: [] },
      mobileUserPage: 1,
      mobileUsersPerPage: 5,
      userFields: [
        // 氏名は折らない（字を大きくしたら「開発サンプル／一郎」と名前の途中で割れた）。表は横に送れる。
        { key: 'name', label: '氏名', _classes: 'text-nowrap' },
        { key: 'email', label: 'メールアドレス' },
        { key: 'roles', label: '割り当て中の権限グループ' },
        { key: 'actions', label: '権限グループを変更' }
      ]
    }
  },
  computed: {
    menusReady () {
      return Boolean(this.viewerContext)
    },
    menusUnknownText () {
      return this.contextFailed ? 'ご契約の情報を読めないため、表示できません' : '読み込み中です'
    },
    companyFeatures () {
      return this.viewerContext && Array.isArray(this.viewerContext.features) ? this.viewerContext.features : []
    },
    canEdit () {
      return Boolean(this.viewerContext) && canEditTenantRoles(this.viewerContext)
    },
    canAssign () {
      return Boolean(this.viewerContext) && canAssignTenantRoles(this.viewerContext)
    },
    canManageProtected () {
      return Boolean(this.viewerContext) && canManageProtectedTenantAdminRoles(this.viewerContext)
    },
    editBlockedReason () {
      if (this.contextFailed) return 'ご契約および権限情報を確認できないため、操作を制限しています。'
      return '権限グループを作成・変更するには、「権限グループの作成・変更」と「利用機能設定の確認」の両方の権限が必要です。'
    },
    assignBlockedReason () {
      if (this.contextFailed) return 'ご契約および権限情報を確認できないため、操作を制限しています。'
      return '社員へ権限グループを割り当てるには、「社員への権限グループ割り当て・解除」の権限が必要です。'
    },
    normalizedUsers () {
      return this.users.map(item => ({
        userId: this.value(item, 'userId', 'user_id'),
        name: this.value(item, 'name'),
        email: this.value(item, 'email'),
        roleCodes: this.arrayValue(item, 'roleCodes', 'role_codes')
      }))
    },
    mobileUserPageCount () {
      return Math.max(1, Math.ceil(this.normalizedUsers.length / this.mobileUsersPerPage))
    },
    mobileUsers () {
      const start = (this.mobileUserPage - 1) * this.mobileUsersPerPage
      return this.normalizedUsers.slice(start, start + this.mobileUsersPerPage)
    },
    normalizedPermissions () {
      return this.permissions.map(item => {
        const code = this.value(item, 'permissionCode', 'permission_code')
        const guide = PERMISSION_GUIDE[code]
        const availableValue = this.value(item, 'featureEnabled', 'feature_enabled')
        return {
          code,
          /*
            **表に無い権限は「未分類」へ出し、名前は DB のまま。**以前は「その他」へ黙って
            落ちていた。表に無い権限は試験が落とす（permissionGuideCoversEveryPermission.spec.js）。
          */
          group: guide ? guide.group : 'unclassified',
          label: guide ? guide.name : (this.value(item, 'permissionName', 'permission_name') || '名称未設定の権限'),
          effect: guide ? effectText(code) : '',
          onlyForRoles: guide && guide.onlyForRoles ? guide.onlyForRoles : null,
          featureCode: this.value(item, 'featureCode', 'feature_code'),
          featureName: this.value(item, 'featureName', 'feature_name'),
          available: availableValue === true || availableValue === 1 || String(availableValue).toLowerCase() === 'true',
          featureSource: this.value(item, 'featureSource', 'feature_source'),
          minimumPlanName: this.value(item, 'minimumPlanName', 'minimum_plan_name'),
          planName: this.value(item, 'planName', 'plan_name')
        }
      }).filter(permission => permission.code)
    },
    permissionByCode () {
      return this.normalizedPermissions.reduce((result, permission) => {
        result[permission.code] = permission
        return result
      }, {})
    },
    currentPlanName () {
      const withPlan = this.normalizedPermissions.find(permission => permission.planName)
      return withPlan ? withPlan.planName : '契約プラン未設定'
    },
    /** Gemini と決めた見出しの順に、権限を並べ直したもの（表に無いものは最後に「未分類」）。 */
    guidedPermissionGroups () {
      return this.groupPermissions(this.normalizedPermissions)
    },
    /**
     * 権限グループの一覧の表（とスマートフォンのカード）の行。編集の小窓と同じ並び（guidedPermissionGroups）に、
     * **権限グループが持っているのに /permissions に無い権限**を足したもの。
     *
     * /permissions は運営専用の権限などを候補から外すが、/roles の permission_codes は r_role_permission を
     * そのまま返す。行を /permissions からだけ作ると、持っている権限が表から黙って消える（歯抜けの逆向き）。
     * 前の一覧（含まれる権限）は、こうした権限も「契約対象外として保持」の枠へ出していたので、同じく灰色の行で出す。
     * **出すのは説明表（PERMISSION_GUIDE）に日本語名がある権限だけ。**名前の無い権限をコードのまま出すと、
     * 利用者向けの画面に内部の権限コードが出る（2026-09-27 の監査）。説明表にあるのは会社の23の権限だけなので、
     * 名前が無いのは運営専用の権限（PROVIDER_BILLING_VIEW など）で、会社の画面では効かない。
     * **編集の小窓には足さない。**選べない権限のチェック欄を増やすと、小窓の動きが変わる。
     */
    matrixPermissionGroups () {
      const orphanCodes = []
      this.normalizedRoles.forEach(role => role.permissionCodes.forEach(code => {
        if (!this.permissionByCode[code] && PERMISSION_GUIDE[code] && !orphanCodes.includes(code)) orphanCodes.push(code)
      }))
      if (!orphanCodes.length) return this.guidedPermissionGroups
      const orphans = orphanCodes.sort().map(code => {
        const guide = PERMISSION_GUIDE[code]
        return {
          code,
          group: guide.group,
          label: guide.name,
          effect: '',
          onlyForRoles: null,
          featureCode: null,
          featureName: null,
          available: false,
          featureSource: null,
          minimumPlanName: null,
          planName: null
        }
      })
      return this.groupPermissions(this.normalizedPermissions.concat(orphans))
    },
    normalizedRoles () {
      return this.roles.map(item => {
        const code = this.value(item, 'roleCode', 'role_code')
        const permissionCodes = this.arrayValue(item, 'permissionCodes', 'permission_codes')
        const activePermissionCodes = permissionCodes.filter(permissionCode => this.isPermissionAvailable(permissionCode))
        return {
          id: this.value(item, 'id'),
          code,
          name: ROLE_LABELS[code] || this.value(item, 'roleName', 'role_name') || '名称未設定の権限グループ',
          scope: this.value(item, 'roleScope', 'role_scope'),
          systemRole: Boolean(this.value(item, 'systemRoleFlg', 'system_role_flg')),
          standardRole: Boolean(this.value(item, 'standardRoleFlg', 'standard_role_flg')),
          customized: Boolean(this.value(item, 'customizedFlg', 'customized_flg')),
          permissionCodes,
          activePermissionCodes,
          menus: menusForGroup(code, activePermissionCodes, this.companyFeatures)
        }
      }).filter(role => role.scope !== 'PLATFORM')
    },
    editingDormantPermissions () {
      return this.editingPermissionCodes
        .map(code => this.permissionByCode[code])
        .filter(permission => permission && !permission.available)
    },
    /**
     * 権限グループを作るときに、まだ入っていない必須項目の名前。
     *
     * **作成ボタンを無効にしている条件と、ここは同じものを見る。**片方だけ
     * 直すと「あと0つです」と出ているのに押せない、というさらに分かりにくい
     * 形になる。
     *
     * 画面に出ている言葉をそのまま使う。roleName と出しても、どこを直せば
     * よいか分からない。
     */
    missingFields () {
      const missing = []
      if (!this.roleForm.roleName) { missing.push('権限グループ名') }
      return missing
    },
    /*
     * 社員に割り当てる選択欄。**名前は権限グループの名前だけ**（利用者 2026-09-27「個人には（権限グループの名前を）
     * つければいい…わけのわからないものにしなければいい」）。前は契約外の権限を持つ権限グループに
     * 「（一部は契約対象外）」を添えていたが、スタンダードの会社では標準の5つのうち4つに付いていた。
     * どの権限が契約外かは、下の権限グループ一覧の表のグレー表示の行で分かる。
     */
    roleOptions () {
      return [{ value: null, label: '権限グループを選択' }].concat(
        this.normalizedRoles.map(role => ({
          value: role.id,
          label: role.name,
          // 最上位の管理者と社内システム管理者は、最上位の管理者だけが割り当て・解除できる。
          // 最上位の管理者は、さらにこの選択欄からは誰も付け外しできない（招待・管理者解除で行う）。
          disabled: INVITATION_ONLY_ROLE_CODES.includes(role.code) ||
            (PROTECTED_ROLE_CODES.includes(role.code) && !this.canManageProtected)
        }))
      )
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    value (item, ...keys) {
      for (const key of keys) {
        if (item && Object.prototype.hasOwnProperty.call(item, key)) return item[key]
        const upper = key.toUpperCase()
        if (item && Object.prototype.hasOwnProperty.call(item, upper)) return item[upper]
      }
      return null
    },
    arrayValue (item, ...keys) {
      const value = this.value(item, ...keys)
      if (Array.isArray(value)) return value
      if (typeof value === 'string') return value.replace(/[{}]/g, '').split(',').filter(Boolean)
      return []
    },
    isPermissionAvailable (code) {
      const permission = this.permissionByCode[code]
      return Boolean(permission && permission.available)
    },
    /** 見出しの順（Gemini と決めた順）に、権限を並べ直す（表に無いものは最後に「未分類」）。 */
    groupPermissions (permissions) {
      const groups = PERMISSION_GROUPS.map(group => Object.assign({}, group, { items: [] }))
      const unclassified = { key: 'unclassified', title: UNCLASSIFIED_GROUP, note: '', items: [] }
      permissions.forEach(permission => {
        const group = groups.find(item => item.key === permission.group) || unclassified
        group.items.push(permission)
      })
      return groups.concat(unclassified).filter(group => group.items.length)
    },
    /**
     * 権限グループの一覧の表で ○ を付けるか。**どの列も、その権限グループが本当に持っている権限で決める。**
     *
     * 最上位の管理者も決め打ちしない。back は最上位の管理者の権限も r_role_permission の行から読み
     * （AccessControlService → selectPermissionCodes）、特別扱いしていない。行がそろっていれば全部 ○ になる
     * （20260723_seed_internal_account_plan_rbac.sql の CROSS JOIN と、あとから足した権限ごとの migration が配る）。
     * 行が欠けた DB で ○ と決め打ちすると、本人の左メニューにも同じ列の「表示されるメニュー」にも出ないのに ○ と見える。
     *
     * 契約に含まれない機能の権限も、権限グループに保存してあれば ○（その行は灰色で出る）。
     */
    roleHasPermission (role, permissionCode) {
      return role.permissionCodes.includes(permissionCode)
    },
    /** 灰色の行の ○ に、指したときに出す言葉（前の一覧で、同じものを囲んでいた枠の見出し）。 */
    markTitle (role, permission) {
      return this.roleHasPermission(role, permission.code) && !permission.available ? DORMANT_MARK_TITLE : null
    },
    /** 左メニューの見出しごとにまとめる（見出しの順は左メニューのまま）。 */
    groupMenus (menus) {
      const groups = []
      menus.forEach(menu => {
        const title = menu.group || ''
        let group = groups.find(item => item.title === title)
        if (!group) {
          group = { title, names: [] }
          groups.push(group)
        }
        group.names.push(menu.name)
      })
      return groups
    },
    /**
     * 社員の左メニュー。割り当て中の権限グループを全部合わせて、左メニューと同じ関数で計算する。
     * **運営（PREIKA側）の権限グループは渡さない。**会社の画面に運営のメニューを出さない。
     */
    menusForUser (user) {
      const roles = this.normalizedRoles.filter(role => user.roleCodes.includes(role.code))
      const permissions = Array.from(new Set([].concat(...roles.map(role => role.activePermissionCodes))))
      return sidebarMenuItems({
        roles: roles.map(role => role.code),
        features: this.companyFeatures,
        permissions
      })
    },
    /** 選び直している最中の「この権限グループで表示されるメニュー」。 */
    previewMenus (roleCode, permissionCodes) {
      const active = (permissionCodes || []).filter(code => this.isPermissionAvailable(code))
      return menusForGroup(roleCode, active, this.companyFeatures)
    },
    requirementText (row) {
      const quoted = row.permissions.map(name => `「${name}」`)
      if (row.kind === 'all') return `${quoted.join('および')}の両方`
      if (row.kind === 'any') return quoted.length > 1 ? `${quoted.join('')}のいずれか` : quoted[0]
      if (row.kind === 'role') {
        return quoted.length
          ? `社内システム管理者は${quoted.join('と')}の両方がオンのときに表示されます。※${row.note}`
          : row.note
      }
      return '権限の設定は不要（全員に表示）'
    },
    permissionDisplayLabel (permission) {
      return `${permission.label}${this.permissionAvailabilityNote(permission)}`
    },
    /** 契約に含まれない権限の名前に添える括弧書き（含まれる権限は空）。表では名前の下の行に分けて出す。 */
    permissionAvailabilityNote (permission) {
      if (permission.available) return ''
      if (permission.minimumPlanName) {
        return `（${permission.minimumPlanName}以上、または個別契約で利用可能）`
      }
      if (permission.featureName) {
        return `（現在の契約では「${permission.featureName}」を利用できません）`
      }
      return '（現在の契約では利用できません）'
    },
    canEditRole (role) {
      if (!this.canEdit) return false
      return !PROTECTED_ROLE_CODES.includes(role.code) || this.canManageProtected
    },
    editRoleBlockedReason (role) {
      if (!this.canEdit) return this.editBlockedReason
      if (PROTECTED_ROLE_CODES.includes(role.code)) {
        return '社内システム管理者の権限は、契約企業管理者（自社最上位）だけが変更できます。'
      }
      return ''
    },
    /**
     * @param quiet 操作のあとの読み直しでは true。
     *   **表を作り直さない。**loading を立てると表が消えて作り直され、1ページ目へ
     *   戻る。2ページ目の人を操作した直後に、その行と結果の文が見えなくなる。
     */
    async loadAll (quiet) {
      if (!quiet) this.loading = true
      this.errorMessage = ''
      try {
        const [users, roles, permissions] = await Promise.all([
          api.getUsers(), api.getRoles(), api.getPermissions()
        ])
        this.permissions = Array.isArray(permissions) ? permissions : []
        this.roles = Array.isArray(roles) ? roles : []
        this.users = Array.isArray(users) ? users : []
        if (this.mobileUserPage > this.mobileUserPageCount) this.mobileUserPage = 1
      } catch (error) {
        this.handleError(error, '権限グループの情報を取得できませんでした。')
      } finally {
        this.loading = false
      }
      if (!quiet) await this.loadViewerContext()
    },
    async loadViewerContext () {
      try {
        this.viewerContext = await getAccessContext()
        this.contextFailed = false
      } catch (error) {
        this.viewerContext = null
        this.contextFailed = true
      }
    },
    setMobileUserPage (page) {
      this.mobileUserPage = Math.min(Math.max(Number(page) || 1, 1), this.mobileUserPageCount)
      this.$nextTick(() => {
        const firstCard = this.$el.querySelector('.mobile-record-card')
        if (firstCard && typeof firstCard.scrollIntoView === 'function') {
          firstCard.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    roleLabel (code) {
      const role = this.normalizedRoles.find(item => item.code === code)
      return role ? role.name : (ROLE_LABELS[code] || '設定済みの権限グループ')
    },
    setSelectedRole (userId, value) {
      this.$set(this.selectedRoleByUser, userId, value)
    },
    selectedRole (user) {
      return this.normalizedRoles.find(role => String(role.id) === String(this.selectedRoleByUser[user.userId]))
    },
    setRowMessage (userId, type, text) {
      this.$set(this.rowMessages, userId, { type, text })
    },
    async assignRole (user) {
      const role = this.selectedRole(user)
      if (!role) return this.setRowMessage(user.userId, 'danger', '追加する権限グループを選択してください。')
      if (user.roleCodes.includes(role.code)) {
        return this.setRowMessage(user.userId, 'danger', 'その権限グループはすでに割り当てられています。')
      }
      await this.runRowAction(
        user,
        () => api.assignRole({ userId: user.userId, roleId: role.id }),
        `「${role.name}」を割り当てました。`
      )
    },
    async removeRole (user) {
      const role = this.selectedRole(user)
      if (!role || !user.roleCodes.includes(role.code)) {
        return this.setRowMessage(user.userId, 'danger', '外したい権限グループを選択してください。')
      }
      await this.runRowAction(
        user,
        () => api.removeRole({ userId: user.userId, roleId: role.id }),
        `「${role.name}」の割り当てを外しました。`
      )
    },
    /**
     * 社員の行で押した操作を実行し、**結果をその行へ出す。**
     * 断られた理由も同じ場所へ出す（押した人が読める場所はそこしかない）。
     */
    async runRowAction (user, action, successText) {
      this.saving = true
      this.$delete(this.rowMessages, user.userId)
      try {
        await action()
        this.setRowMessage(user.userId, 'success', successText)
        await this.loadAll(true)
      } catch (error) {
        this.setRowMessage(user.userId, 'danger', this.errorText(error))
      } finally {
        this.saving = false
      }
    },
    openCreateRole () {
      /*
        **ここで roleForm を空にしない。**

        以前はこの行で初期化していた。作成に失敗すると赤字はモーダルの外に
        出るので、利用者は読むためにいったん閉じる。そして開き直した瞬間、
        選んだ権限が全部消えていた。**やり直そうとするほど作業が消える。**

        空にするのは、権限グループを作り終えたときだけでよい。作りかけが残っていても
        名前と権限はそのまま画面に見えているので、迷いようがない。
      */
      this.roleFormError = ''
      this.roleModal = true
    },
    /*
     * 送っているあいだは閉じない（2026-09-26 監査）。キャンセルも見出しの×もここへ来る。
     * 閉じると、あとから届いた理由を出す場所がなくなる。
     */
    closeCreateRole () {
      if (this.saving) return
      this.roleModal = false
    },
    closePermissionEdit () {
      if (this.saving) return
      this.permissionModal = false
    },
    openPermissionEdit (role) {
      if (role.code === 'TENANT_OWNER') {
        this.$set(this.roleMessages, role.code, {
          type: 'danger',
          text: '契約企業管理者（自社最上位）の権限は変更できません。'
        })
        return
      }
      this.editingRole = role
      this.editingPermissionCodes = role.permissionCodes.slice()
      this.permissionFormError = ''
      this.permissionModal = true
    },
    togglePermission (list, permission, checked) {
      // グレー表示の権限はチェック欄ごと押せない。念のための最後の壁。
      if (!permission.available) return
      const index = list.indexOf(permission.code)
      if (checked && index < 0) list.push(permission.code)
      if (!checked && index >= 0) list.splice(index, 1)
    },
    async createRole () {
      this.roleFormError = ''
      if (!this.roleForm.roleName) {
        // ボタンを無効にしてあるので普通は通らない。念のための最後の壁。
        this.roleFormError = '権限グループ名を入力してください。'
        return
      }
      const payload = Object.assign({}, this.roleForm, {
        permissionCodes: this.roleForm.permissionCodes.filter(code => this.isPermissionAvailable(code))
      })
      const createdName = this.roleForm.roleName
      await this.execute(
        () => api.createRole(payload),
        `権限グループ「${createdName}」を作成しました。`,
        () => {
          this.roleModal = false
          // 作り終えたので、ここで初めて空にする。
          this.roleForm = { roleName: '', description: '', permissionCodes: [] }
        },
        // **断られた理由もモーダルの中へ。**外へ出しても覆われていて読めない。
        error => { this.roleFormError = this.errorText(error) }
      )
    },
    async savePermissions () {
      this.permissionFormError = ''
      const role = this.editingRole
      this.saving = true
      try {
        await api.replaceRolePermissions({
          roleId: role.id,
          permissionCodes: this.editingPermissionCodes
        })
        this.permissionModal = false
        /*
          **成功の文は、編集した行のそばへ。**画面の上の帯へ出すと、
          小窓を閉じたあと利用者が見ているのは下の表なので、見えない。
        */
        this.$set(this.roleMessages, role.code, { type: 'success', text: `「${role.name}」の権限を更新しました。` })
        await this.loadAll(true)
      } catch (error) {
        // **断られた理由もモーダルの中へ。**外へ出しても覆われていて読めない。
        this.permissionFormError = this.errorText(error)
      } finally {
        this.saving = false
      }
    },
    /**
     * @param onError 赤字の置き場所を変えたいときに渡す。
     *   **モーダルの中で失敗したものは、中へ出さないと読めない。**
     */
    async execute (action, message, after, onError) {
      this.saving = true
      this.errorMessage = ''
      try {
        await action()
        if (after) after()
        this.successMessage = message
        await this.loadAll(true)
      } catch (error) {
        if (onError) {
          onError(error)
        } else {
          this.handleError(error)
        }
      } finally {
        this.saving = false
      }
    },
    /** 画面へ出してよい文言を選ぶ。出す場所は呼ぶ側が決める。 */
    errorText (error, fallback) {
      const responseMessage = error && error.response && error.response.data &&
        (error.response.data.message)
      // axiosが付ける英語のmessageは画面へ出さない。
      const ownMessage = error && !error.isAxiosError ? error.message : ''
      return responseMessage || ownMessage || fallback || '処理に失敗しました。'
    },
    handleError (error, fallback) {
      this.errorMessage = this.errorText(error, fallback)
    }
  }
}
</script>

<style scoped>
.tenant-access-page {
  min-width: 0;
  padding: 28px;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}
.page-header__copy {
  min-width: 0;
}
.page-header h1 {
  margin-bottom: 8px;
}
.page-header p {
  max-width: 760px;
  margin-bottom: 0;
  overflow-wrap: anywhere;
}
.page-header__action {
  flex: 0 0 auto;
  min-height: 42px;
}
.eyebrow {
  margin-bottom: 4px;
  color: #64748b;
  font-weight: 700;
  letter-spacing: .16em;
}
.plan-scope-alert {
  line-height: 1.7;
}
.access-card {
  min-width: 0;
  margin-bottom: 18px;
}
.responsive-table-shell {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
/*
  表の最小の幅。画面の幅 1280px のとき、表の入る幅は約 841px。これより大きくすると、
  右端の「権限グループを変更」の欄が枠の外へ出て、横へ送らないと見えない（2026-10-11 に 860px → 760px）。
*/
.responsive-table-shell::v-deep table {
  min-width: 760px;
}
/*
  行の中身の長さが欄ごとに大きく違うので、上へそろえる（中央だと短い欄の上が空いて見える）。
  一覧の画面全体に中央寄せの決まり（.preika-route-view--list .table:not(.search-table) > tbody > tr > td、
  強さ 0,3,3）があるので、それより強くする。
*/
.tenant-access-page .responsive-table-shell::v-deep .table > tbody > tr > td {
  vertical-align: top;
}
.desktop-role-controls {
  min-width: 230px;
}
.desktop-role-buttons {
  display: flex;
  gap: 8px;
}
.row-message {
  margin: 8px 0 0;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: .875rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
}
.row-message--success {
  color: #0f5132;
  background: #d1e7dd;
}
.row-message--danger {
  color: #842029;
  background: #f8d7da;
}
.role-badge-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}
.role-badge-list .badge {
  white-space: normal;
  text-align: left;
  overflow-wrap: anywhere;
}
/*
  権限グループの一覧の表（権限を縦、権限グループを横）。

  この画面は名前が「管理」で終わるので、一覧の画面全体の表の決まり（summary-presentation.css の
  .preika-route-view--list .table:not(.search-table) …、強さ 0,3,3〜0,4,3）が掛かる。
  見出しの文字が 10px になり、偶数行と指した行に色が付く。灰色の行が偶数行の色や指した色に
  負けないように、ここは .tenant-access-page .responsive-table-shell .role-matrix から書いて強くする。
*/
.tenant-access-page .responsive-table-shell .role-matrix {
  margin-bottom: 0;
}
.tenant-access-page .responsive-table-shell .role-matrix > thead > tr > th {
  font-size: .8125rem;
  line-height: 1.45;
  vertical-align: bottom;
  overflow-wrap: anywhere;
}
/*
  権限グループの欄の最小の幅。下の段「表示されるメニュー」のメニュー名（いちばん長いのは
  「予定超過残業の申請一覧、」の12字・13px）が名前の途中で折れない幅にする（112px では
  「プロジェクト金額設／定」と割れた）。表は横に送れる（responsive-table-shell）。
*/
.tenant-access-page .responsive-table-shell .role-matrix .role-matrix__role-name {
  min-width: 176px;
  text-align: center;
}
.tenant-access-page .responsive-table-shell .role-matrix .role-matrix__corner,
.tenant-access-page .responsive-table-shell .role-matrix .role-matrix__permission,
.tenant-access-page .responsive-table-shell .role-matrix .role-matrix__footer-label {
  /* 権限グループが多くて横へ送っても、どの権限の行かが分かるように左端に留める。 */
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 220px;
  max-width: 300px;
  background: #fff;
  text-align: left;
  /*
    全体の決まり（style.scss の th[scope="row"] … nowrap・keep-all）を外して折り返す。
    外さないと、灰色の行の長い名前（「…（ビジネス以上、または個別契約で利用可能）」）が1行のまま
    欄からはみ出し、隣の ○ に重なって末尾が読めなかった（2026-09-27 に手元の画面で確認）。
  */
  white-space: normal;
  /*
    折り返すのは語の切れ目（「、」の後ろなど）だけにする。normal だと「ビジネス以｜上」「利用可｜能」のように
    語の途中で切れた（2026-09-27 に手元の画面で確認）。1語が欄より長いときだけ overflow-wrap で切る。
  */
  word-break: keep-all;
}
.tenant-access-page .responsive-table-shell .role-matrix .role-matrix__availability {
  display: block;
  font-size: .75rem;
  font-weight: 500;
}
.tenant-access-page .responsive-table-shell .role-matrix .role-matrix__corner {
  z-index: 2;
  background: #eef6fa;
}
.tenant-access-page .responsive-table-shell .role-matrix .role-matrix__permission {
  color: #334155;
  font-size: .8125rem;
  font-weight: 600;
  line-height: 1.55;
  vertical-align: middle;
  overflow-wrap: anywhere;
}
.tenant-access-page .responsive-table-shell .role-matrix > tbody > tr.role-matrix__heading > th {
  padding: 8px 12px;
  border-color: #d6e3eb;
  background: #e6f0f6;
  color: #17384d;
  font-size: .8125rem;
  font-weight: 800;
}
/*
  見出しの文字だけを左端に留める。欄（th）は表の幅いっぱいなので、欄に sticky を付けても動かない。
  inline-block にして文字の幅に縮め、その箱を留める。添え書きが長いので、狭い画面でも見える幅で折り返す。
*/
.tenant-access-page .responsive-table-shell .role-matrix .role-matrix__heading-text {
  position: sticky;
  left: 12px;
  display: inline-block;
  max-width: 640px;
}
.role-matrix__note {
  display: block;
  margin: 2px 0 0;
  color: #64748b;
  font-size: .75rem;
  font-weight: 400;
  line-height: 1.55;
}
.tenant-access-page .responsive-table-shell .role-matrix > tbody > tr > td.role-matrix__mark {
  padding: 6px 8px;
  font-size: 1rem;
  line-height: 1.4;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}
.role-matrix__mark--has {
  color: #1f5f8b;
  font-weight: 800;
}
.role-matrix__mark--not {
  color: #94a3b8;
}
/*
  契約に含まれない機能の権限の行。編集の小窓のグレー表示（.permission-option--unavailable）と同じ地の色・文字の色。
*/
.tenant-access-page .responsive-table-shell .role-matrix > tbody > tr.role-matrix__row--unavailable > th,
.tenant-access-page .responsive-table-shell .role-matrix > tbody > tr.role-matrix__row--unavailable > td,
.mobile-permission-row.role-matrix__row--unavailable {
  background: #f4f6f8;
  color: #677984;
}
.role-matrix__row--unavailable .role-matrix__mark--has,
.role-matrix__row--unavailable .role-matrix__mark--not {
  color: #677984;
}
/*
  スマートフォンのカードでは、行が置かれる地（.mobile-section の #f5f9fc）と灰色（#f4f6f8）がほとんど同じで、
  地の色だけでは見分けられない。編集の小窓のグレー表示と同じ点線の枠も付ける（パソコンの表は白い行の中なので地の色で分かる）。
*/
.mobile-permission-row.role-matrix__row--unavailable {
  border: 1px dashed #d6dde2;
}
.tenant-access-page .responsive-table-shell .role-matrix > tfoot > tr > td,
.tenant-access-page .responsive-table-shell .role-matrix > tfoot > tr > th {
  padding: 10px 8px;
  background: #fff;
  vertical-align: top;
  font-size: .8125rem;
}
.mobile-permission-group {
  display: grid;
  gap: 2px;
  min-width: 0;
  margin-bottom: 12px;
}
.mobile-permission-group:last-child {
  margin-bottom: 0;
}
.mobile-permission-group > strong {
  margin-bottom: 4px;
  color: #334155;
  font-size: .8125rem;
}
.mobile-permission-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 6px 8px;
  /* 灰色の行の点線の枠と同じ太さを、ほかの行にも透明で持たせる（枠の有無で文字の位置がずれない）。 */
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: .8125rem;
  line-height: 1.5;
}
.mobile-permission-row__name {
  min-width: 0;
  overflow-wrap: anywhere;
}
.permission-group {
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}
.permission-group:last-child {
  border-bottom: 0;
}
.permission-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px 16px;
}
.permission-option {
  min-width: 0;
}
.permission-option--unavailable {
  padding: 8px 10px;
  border: 1px dashed #d6dde2;
  border-radius: 9px;
  background: #f4f6f8;
  opacity: .72;
}
.tenant-access-page::v-deep .permission-option--unavailable .custom-control-label {
  color: #677984;
  cursor: not-allowed;
}
.permission-group__note {
  margin: -4px 0 8px;
  color: #64748b;
  font-size: .8125rem;
  line-height: 1.6;
}
.permission-item {
  min-width: 0;
}
.permission-item__effect,
.permission-item__warning {
  margin: 4px 0 0 1.5rem;
  font-size: .8125rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
}
.permission-item__effect {
  color: #475569;
}
.permission-item__warning {
  color: #8a5a00;
}
.menu-details {
  margin-top: 8px;
}
.menu-details summary {
  color: #1f5f8b;
  font-size: .8125rem;
  cursor: pointer;
}
.menu-group-list {
  display: grid;
  gap: 6px;
  min-width: 0;
  margin-top: 6px;
}
.menu-group-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  min-width: 0;
  font-size: .8125rem;
  line-height: 1.55;
}
.menu-group-row strong {
  color: #334155;
}
.menu-group-row span {
  min-width: 0;
  overflow-wrap: anywhere;
}
/*
  メニューの名前は1つずつ折らない塊にし、「、」の後ろでだけ折る（字を大きくしたら「経費精／算」のように
  名前の途中で折れた）。欄より長い名前だけは、塊の中で折れる（max-width）。
*/
.menu-group-row .menu-group-row__name {
  display: inline-block;
  max-width: 100%;
  /* 欄より長い名前（権限グループの表の狭い欄）は、文節で折る（「プロジェクト／金額設定」）。Chrome・Edge で効く。 */
  word-break: auto-phrase;
}
.menu-preview {
  margin: 16px 0;
  padding: 12px 14px;
  border: 1px solid #cfe0ea;
  border-radius: 10px;
  background: #f5f9fc;
}
.menu-requirement-table {
  margin-bottom: 0;
}
.menu-requirement-table td,
.menu-requirement-table th {
  font-size: .875rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
}
/*
  見出しと対象メニューは短い名前なので折らない（3列目の長い文に押されて「人員・休／暇」
  「プロジェクト金額設／定」と名前の途中で割れていた）。折るのは3列目（必要な権限）だけ。
  狭い画面では、表を横に送る（responsive-table-shell）。
*/
.menu-requirement-table td:nth-child(1),
.menu-requirement-table td:nth-child(2) {
  white-space: nowrap;
  overflow-wrap: normal;
}
/*
  表とカードは同じ中身で、画面幅に応じて片方だけを出す。

  切り替えは .mobile-only / .desktop-only が持っている。ところが同じ div に
  付けた一覧クラスにも display があり、後ろに書いた方が勝つため、広い画面
  でも表の下にカードが並んでいた。組み合わせで明示して、書き順に左右され
  ないようにする。
*/
.mobile-only {
  display: none;
}
.mobile-only.mobile-record-list {
  display: none;
}
.mobile-record-list {
  display: grid;
  gap: 14px;
}
.mobile-record-card {
  min-width: 0;
  padding: 16px;
  border: 1px solid #d8e4ec;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(28, 68, 91, .07);
}
.mobile-record-card__header {
  display: grid;
  gap: 4px;
  min-width: 0;
  margin-bottom: 14px;
}
.mobile-record-card__header strong {
  color: #17384d;
  font-size: 17px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.mobile-record-card__header span {
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.mobile-section {
  min-width: 0;
  margin-bottom: 14px;
  padding: 13px;
  border-radius: 11px;
  background: #f5f9fc;
}
.mobile-section:last-child {
  margin-bottom: 0;
}
.mobile-section h3,
.mobile-section label {
  display: block;
  margin: 0 0 10px;
  color: #526b7b;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .03em;
}
.mobile-section--controls {
  background: #fff;
  border: 1px solid #e0e9ef;
}
.mobile-button-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.mobile-button-grid .btn,
.mobile-primary-action {
  width: 100%;
  min-height: 46px;
  margin: 0;
  white-space: normal;
}
.mobile-role-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  margin-bottom: 14px;
}
.mobile-role-header strong {
  min-width: 0;
  color: #17384d;
  font-size: 17px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.mobile-role-header .badge {
  flex: 0 0 auto;
  white-space: nowrap;
}
.protected-role-note {
  padding: 11px 12px;
  border-radius: 10px;
  background: #f2f5f7;
  color: #667985;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
}
.mobile-empty-state {
  padding: 30px 14px;
  border: 1px dashed #cbd8e1;
  border-radius: 12px;
  color: #687d8a;
  text-align: center;
}
.mobile-pager {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #dfe8ee;
  border-radius: 12px;
  background: #f8fbfd;
}
.mobile-pager .btn {
  min-height: 44px;
  margin: 0;
}
.mobile-pager span {
  min-width: 54px;
  color: #455f70;
  font-weight: 800;
  text-align: center;
  white-space: nowrap;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
}

@media (max-width: 767.98px) {
  .tenant-access-page {
    padding: 12px 0 112px;
  }
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    margin-bottom: 16px;
  }
  .page-header h1 {
    font-size: 28px;
    line-height: 1.25;
  }
  .page-header__action {
    width: 100%;
    min-height: 46px;
    margin: 0;
    white-space: normal;
  }
  .desktop-only {
    display: none !important;
  }
  .mobile-only {
    display: block;
  }
  .mobile-only.mobile-record-list {
    display: grid;
  }
  .tenant-access-page::v-deep .card-header {
    padding: 14px;
  }
  .tenant-access-page::v-deep .card-body {
    padding: 12px;
  }
  .mobile-role-header {
    flex-wrap: wrap;
  }
  .menu-group-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
  }
  .permission-grid {
    grid-template-columns: minmax(0, 1fr);
    max-height: none;
    overflow: visible;
  }
  .tenant-access-page::v-deep .permission-option .custom-control-label {
    white-space: normal;
    line-height: 1.55;
    overflow-wrap: anywhere;
  }
  .modal-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .modal-actions .btn {
    width: 100%;
    min-height: 46px;
    margin: 0;
    white-space: normal;
  }
  .tenant-access-page::v-deep .access-modal .modal-dialog {
    width: auto;
    max-width: calc(100vw - 16px);
    margin: 8px;
  }
  .tenant-access-page::v-deep .access-modal .modal-body {
    max-height: calc(100vh - 190px);
    overflow-y: auto;
  }
  .tenant-access-page::v-deep .access-modal .modal-footer {
    padding: 12px;
  }
}

@media (min-width: 768px) {
  .desktop-only {
    display: block;
  }
}
</style>
